import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, throwError, of, interval } from 'rxjs';
import { map, catchError, mergeMap, filter, take, concatMap } from 'rxjs/operators';
import { ConfigApiErrorTypes, ConfigApiError } from './config.errors';
import omitBy from 'lodash-es/omitBy';
import isNull from 'lodash-es/isNull';
import cloneDeep from 'lodash-es/cloneDeep';
import color from 'color';
import { Config, ConfigValues, Palette, Corpus } from './models/config.model';
import { ConfigParameters, ConfigSettings } from './models/config-settings.model';
import { Store } from '@ngrx/store';
import { getSelectedAvatarProperties } from './config.selectors';
import { toHttpParams } from '../project/helpers/to-http-params';
import { generateDeployLink } from './helpers/generate-deploy-link';
import { ProjectUndeployDialogComponent } from 'src/app/shared/dialogs/project-undeploy-dialog/project-undeploy-dialog.component';
import { DialogDisplayService } from 'studio-components';
import { ProjectSummary } from '../project/models/project-summary.model';

type DeployConfigRequest = { configId: number; type: 'DEPLOY' };

type UndeployConfigRequest = { configId: number; type: 'UNDEPLOY' };

export enum PreviewTaskType {
  Passive = 'DEPLOY_PASSIVE_PREVIEW',
  Interactive = 'DEPLOY_INTERACTIVE_PREVIEW',
}

type DeployPreviewConfigRequest = {
  type: PreviewTaskType;
  payload: { configValues: ConfigValues; previewProjectId: string | null };
};

export type DeployRequest =
  | DeployConfigRequest
  | DeployPreviewConfigRequest
  | UndeployConfigRequest;

export type ConfigTaskError = {
  name: string;
  message: string;
  code: string;
};

export type ConfigTaskType = DeployRequest['type'];
export type ConfigTask = {
  id: number;
  status: string;
  type: ConfigTaskType;
  error?: ConfigTaskError;
};

export type DeployPreviewConfigTask = ConfigTask & {
  values: {
    jwt: string;
  };
  previewProjectId: string;
};

export type DeployTask = ConfigTask | DeployPreviewConfigTask;

export const RETRY_DELAY: any = (environment as any).name === 'test' ? 100 : 500;

@Injectable()
export class ConfigService {
  constructor(
    private http: HttpClient,
    private store: Store<any>,
    private dialogDisplayService: DialogDisplayService,
  ) {}

  loadConfig(configId: number): Observable<Config> {
    const configsUrl = `${environment.apiConfig.url}/configs/${configId}`;
    return this.http
      .get<Config>(configsUrl)
      .pipe(
        catchError((err) =>
          throwError(new ConfigApiError(ConfigApiErrorTypes.LoadConfigError, err)),
        ),
      );
  }

  loadLatestConfigForProject(projectId: number) {
    const url = `${environment.apiConfig.url}/configs`;
    const params = new HttpParams()
      .set('filter[where][projectId]', `${projectId}`)
      .set('filter[order]', 'datetimeAdded DESC')
      .set('filter[limit]', '1');

    return this.http.get(url, { params }).pipe(
      // the query returns an array, we only want the first (and only) item of that array
      map((result) => result[0]),
    );
  }

  loadDeployedConfigForProject(projectId: number) {
    const url = `${environment.apiConfig.url}/configs`;
    const params = new HttpParams()
      // must include deleted records to handle the case where a project has been edited since deployment and
      // not redeployed - in this case the deployed config we require has been deleted and replaced by a new one
      .set('option[showAll]', 'true')
      .set('filter[where][projectId]', `${projectId}`)
      .set('filter[where][isDeployed]', 'true')
      .set('filter[order]', 'datetimeAdded DESC')
      .set('filter[limit]', '1');

    return this.http.get(url, { params }).pipe(
      // the query returns an array, we only want the first (and only) item of that array
      map((result) => result[0]),
    );
  }

  /**
   * Creates new config based on given config
   *
   * TODO: service should not be doing this logic.
   * it should expect a clean config to be passed to it, so that
   * it can send the config to the server without any additional
   * changes being necessary.
   */
  public createNewConfig(
    configSettings: ConfigSettings,
    configParameters: Partial<ConfigParameters> = {},
  ): Observable<Config> {
    const configsUrl = `${environment.apiConfig.url}/configs`;

    return of(configSettings).pipe(
      map(cloneDeep),
      concatMap((config) => this.sanitizeConfig(config as Config)),
      concatMap((config) =>
        // send to config service
        this.http
          .post<Config>(configsUrl, config, { params: toHttpParams(configParameters) })
          .pipe(catchError((err) => this.createConfigErrorHandler(err))),
      ),
    );
  }

  // handle errors from POST request
  private createConfigErrorHandler(error: HttpErrorResponse): Observable<Config> {
    const errorType =
      error.status === 409
        ? ConfigApiErrorTypes.ConfigConflictError
        : ConfigApiErrorTypes.CreateConfigError;
    return throwError(new ConfigApiError(errorType, error));
  }

  // sanitize against the schema for the selected avatar
  private sanitizeAgainstSchema(configValues: ConfigValues): Observable<ConfigValues> {
    return this.store.select(getSelectedAvatarProperties).pipe(
      take(1),
      map((schemaProperties) => {
        if (!schemaProperties?.assets) {
          delete configValues.assets;
        }

        return configValues;
      }),
    );
  }

  // remove null values that might cause server errors
  private sanitizeNulls(configValues: ConfigValues): ConfigValues {
    configValues.corpus = this.sanitizeCorpus(configValues.corpus);
    configValues.assets = this.sanitizeAssets(configValues.assets);

    return configValues;
  }

  private sanitizeCorpus(corpus: Corpus): Corpus {
    if (!corpus) {
      return corpus;
    }

    return omitBy(corpus, isNull) as Corpus;
  }

  private sanitizeAssets(assets: any): any {
    if (!assets) {
      return assets;
    }

    return omitBy(assets, isNull) as any;
  }

  private sanitizeConfigValues(configValues: ConfigValues) {
    return of(configValues).pipe(
      map((cv) => this.sanitizeNulls(cv)),
      concatMap((cv) => this.sanitizeAgainstSchema(cv)),
    );
  }

  private sanitizeConfig(config: Config) {
    return of(config).pipe(
      map((c) => c.values),
      concatMap((c) => this.sanitizeConfigValues(c)),
      map((configValues) => ({ ...config, values: configValues } as Config)),
    );
  }

  private getDeployStatus<Task extends DeployTask>(taskId: number): Observable<Task> {
    const tasksUrl = `${environment.apiConfig.url}/tasks/${taskId}`;

    return this.http.get<Task>(tasksUrl).pipe(
      mergeMap((task) => {
        if (task.status === 'ERROR') {
          const errorType =
            task.type === 'DEPLOY'
              ? ConfigApiErrorTypes.DeployConfigTaskPollingError
              : ConfigApiErrorTypes.DeployPreviewConfigTaskPollingError;
          return throwError(new ConfigApiError(errorType, task.error));
        }
        return of(task);
      }),
    );
  }

  private pollDeployStatus<Task extends DeployTask>(
    taskId: number,
    taskType: 'DEPLOY' | 'DEPLOY_PREVIEW',
  ) {
    return interval(RETRY_DELAY).pipe(
      mergeMap(() => this.getDeployStatus<Task>(taskId)),
      filter((task) => task.status === 'DONE'),
      take(1),
      catchError((err) => {
        const errorType =
          taskType === 'DEPLOY'
            ? ConfigApiErrorTypes.DeployConfigTaskPollingError
            : ConfigApiErrorTypes.DeployPreviewConfigTaskPollingError;

        return throwError(new ConfigApiError(errorType, err.error));
      }),
    );
  }

  private createDeployTask<Task extends DeployTask>(request: DeployRequest): Observable<Task> {
    const tasksUrl = `${environment.apiConfig.url}/tasks`;

    return this.http.post<Task>(tasksUrl, request).pipe(
      catchError((err) => {
        const errorType =
          request.type === 'DEPLOY'
            ? ConfigApiErrorTypes.DeployConfigTaskCreationError
            : ConfigApiErrorTypes.DeployPreviewConfigTaskCreationError;
        return throwError(new ConfigApiError(errorType, err));
      }),
    );
  }

  public deployConfig(
    config: Config,
    keyName: string,
    deploymentSubdomain: string,
  ): Observable<string> {
    return this.createDeployTask<DeployTask>({ configId: config.id, type: 'DEPLOY' }).pipe(
      mergeMap((task) => this.pollDeployStatus(task.id, 'DEPLOY')),
      map(() => generateDeployLink(keyName, deploymentSubdomain)),
    );
  }

  public deployPreviewConfig(
    configValues: ConfigValues,
    taskType: PreviewTaskType,
    previewProjectId: string | null,
  ) {
    return of(configValues).pipe(
      map(cloneDeep),
      concatMap((cv) => this.sanitizeConfigValues(cv)),
      concatMap((cv) =>
        this.createDeployTask<DeployPreviewConfigTask>({
          type: taskType,
          payload: { configValues: cv, previewProjectId },
        }),
      ),
    );
  }

  public pollDeployPreviewConfig(taskId: number) {
    return this.pollDeployStatus<DeployPreviewConfigTask>(taskId, 'DEPLOY_PREVIEW').pipe(
      map((task) => ({ jwt: task.values.jwt })),
    );
  }

  public calculatePalettes(palette): Palette | null {
    if (!palette) {
      return null;
    }

    const input = color(palette);
    const light = input.lighten(0.3);
    const dark = input.darken(0.3);

    // Contrast color should be consistent across all variations of
    // the input color. There should not be a mix of dark/light contrast.
    // The contrast color to use should be decided based on the middle color.
    const darkGrey = '#2a2635';
    const white = '#ffffff';
    const contrastColor = input.isLight() ? darkGrey : white;

    return {
      base: {
        100: light.string(),
        500: palette,
        900: dark.string(),
      },
      contrast: {
        100: contrastColor,
        500: contrastColor,
        900: contrastColor,
      },
    };
  }

  private createUndeployTask(request: UndeployConfigRequest): Observable<ConfigTask> {
    const tasksUrl = `${environment.apiConfig.url}/tasks`;

    return this.http
      .post<ConfigTask>(tasksUrl, request)
      .pipe(
        catchError((err) =>
          throwError(new ConfigApiError(ConfigApiErrorTypes.UndeployConfigTaskCreationError, err)),
        ),
      );
  }

  private pollUndeployStatus(taskId: number) {
    return interval(RETRY_DELAY).pipe(
      mergeMap(() => this.getDeployStatus<ConfigTask>(taskId)),
      filter((task) => task.status === 'DONE'),
      take(1),
      catchError((err) =>
        throwError(new ConfigApiError(ConfigApiErrorTypes.UndeployConfigTaskPollingError, err)),
      ),
    );
  }

  public async showUndeployConfirmation(project: ProjectSummary) {
    const dialogRef = this.dialogDisplayService.open(
      ProjectUndeployDialogComponent,
      { theme: 'light' },
      project,
    );

    return await dialogRef.afterClosed().toPromise();
  }

  public undeployConfig(configId: number): Observable<ConfigTask> {
    return this.createUndeployTask({ configId, type: 'UNDEPLOY' });
  }

  public pollUndeployConfig(taskId: number) {
    return this.pollUndeployStatus(taskId).pipe(map((task) => task));
  }
}
